import { useCallback, useEffect, useRef } from 'react';

import type { PlacesSessionToken } from 'hooks/geolocation/types';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { IPlaceData, parsePlaceAddress } from 'utils/geolocation';

type GoogleGeocode = google.maps.Geocoder;
type GeocoderResult = google.maps.GeocoderResult;

function fetchPlaceDetailsWithSession(
  placeId: string,
  sessionToken: PlacesSessionToken,
  placesService: google.maps.places.PlacesService
): Promise<IPlaceData | undefined> {
  return new Promise(resolve => {
    const request: google.maps.places.PlaceDetailsRequest = {
      placeId,
      sessionToken,
      fields: ['geometry', 'address_components'],
    };

    placesService.getDetails(request, (place, status) => {
      if (status !== window?.google?.maps?.places?.PlacesServiceStatus?.OK || !place) {
        return resolve(undefined);
      }

      const location = place.geometry?.location;
      if (!location) {
        return resolve(undefined);
      }

      const placeData: IPlaceData = {
        coordinates: {
          lat: location.lat(),
          lng: location.lng(),
        },
        address: parsePlaceAddress(place.address_components ?? [], {
          useISOCountry: true,
        }),
      };
      resolve(placeData);
    });
  });
}

export default function usePlaceIdDetails() {
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const geocoderRef = useRef<GoogleGeocode | undefined>(undefined);
  const placesServiceRef = useRef<google.maps.places.PlacesService | undefined>(undefined);

  useEffect(() => {
    if (!libraryLoaded || typeof document === 'undefined') {
      return;
    }

    const div = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(div);

    return () => {
      placesServiceRef.current = undefined;
    };
  }, [libraryLoaded]);

  const getDetails = useCallback(
    (placeId?: string, sessionToken?: PlacesSessionToken): Promise<IPlaceData | undefined> => {
      return new Promise(resolve => {
        if (!placeId || !libraryLoaded) {
          resolve(undefined);
          return;
        }

        const usePlaceDetails =
          sessionToken && typeof sessionToken !== 'string' && !!placesServiceRef.current;

        if (usePlaceDetails) {
          fetchPlaceDetailsWithSession(placeId, sessionToken, placesServiceRef.current!).then(
            resolve
          );
          return;
        }

        if (!geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }

        geocoderRef.current.geocode({ placeId }, (results, status) => {
          if (status !== window?.google?.maps?.GeocoderStatus?.OK) {
            return resolve(undefined);
          }

          const firstResult: GeocoderResult | null = results?.length ? results[0] : null;
          if (!firstResult?.geometry?.location) {
            return resolve(undefined);
          }

          const { geometry, address_components } = firstResult;
          const placeData: IPlaceData = {
            coordinates: {
              lat: geometry.location.lat(),
              lng: geometry.location.lng(),
            },
            address: parsePlaceAddress(address_components, {
              useISOCountry: true,
            }),
          };
          resolve(placeData);
        });
      });
    },
    [libraryLoaded]
  );

  return getDetails;
}
